<template>
  <v-skeleton-loader
    :loading="loading"
    type="table-thead, table-row-divider@10"
  >
    <v-data-table
      :key="tableKey"
      class="table-striped custom-pagination"
      :headers="headers"
      :items="inventory"
      :custom-sort="customSort"
      :custom-filter="customFilter"
      :search="search"
      :sort-by="['name']"
      :items-per-page.sync="tableState.itemsPerPage"
      :footer-props="footerProps"
      :loading="loading"
    >
      <template #item.action="{ item }">
        <ButtonView v-if="item.type === 'unit'" @click="gotoUnit(item.id)" />
      </template>

      <template #item.name="{ item }">
        <span v-if="item.type === 'unit'" class="d-flex align-center">
          <PossiblePMS as-icon :sync-obj="item">
            <span>{{ item.name }}</span>
          </PossiblePMS>
          <div v-if="item.isActiveDemo" class="d-flex align-center">
            <v-icon right small color="orange">fas fa-user-clock</v-icon>
            <v-chip x-small color="orange" text-color="white" class="ml-1">
              Demo
            </v-chip>
          </div>
          <v-chip
            v-if="shouldShowProLabel(item)"
            x-small
            color="#1565c0"
            text-color="white"
            class="ml-2"
          >
            <v-icon left small color="#ffd700">mdi-crown</v-icon> PRO
          </v-chip>
        </span>
      </template>

      <template #item.devicesName="{ item: { devices } }">
        <v-list dense color="transparent">
          <v-list-item
            v-for="device in devices"
            :key="device.id || device.shortId || device.externalId"
            class="ml-0 pl-0"
          >
            <v-list-item-content>
              <div class="d-flex align-left">
                {{ formatDeviceName(device) }}
              </div>
              <v-badge
                v-if="device.labels?.isRefresh"
                color="orange"
                content="Refresh"
                class="align-left"
                style="justify-content: start"
                inline
              />
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template #item.devicesHealth="{ item: { devices } }">
        <v-list dense color="transparent">
          <v-list-item
            v-for="device in devices"
            :key="device.id || device.shortId || device.externalId"
            class="ml-0 pl-0"
          >
            <v-list-item-content>
              <DeviceGaugeCluster :device="device" />
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template #item.devicesLastMotion="{ item: { devicesLastMotion } }">
        <v-list dense color="transparent">
          <v-list-item
            v-for="(lastMotion, index) in devicesLastMotion"
            :key="`motion-${index}`"
            class="ml-0 pl-0"
          >
            <v-list-item-content>
              {{ lastMotion }}
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template #item.manage="{ item: { type, devices, name } }">
        <MenuManageDevice
          v-for="device in devices"
          :key="device.id || device.shortId || device.externalId"
          :device="device"
          :unit-name="type === 'unit' ? name : undefined"
          v-on="$listeners"
        />
      </template>

      <template #footer.prepend>
        <ButtonExportCSV
          class="order-last"
          :table-data="inventory"
          :headers="headers"
          filename="SD_Inventory_PreInstalled"
          :trigger-show-all="triggerShowAllForExport"
          :wait-for-data="waitForExportData"
          :after-export="handleAfterExport"
          :get-table-data="getCurrentInventoryData"
        />
        <ButtonPrint
          class="order-last"
          :trigger-show-all="triggerShowAllForPrint"
          :wait-for-data="waitForDataToLoad"
          :after-print="handleAfterPrint"
        />
      </template>
    </v-data-table>

    <div
      v-if="!search && tableState.itemsPerPage > 0"
      class="d-flex justify-center align-center my-4 office-pagination-arrow"
    >
      <div class="mr-4 d-flex align-center">
        <span class="caption">
          {{ paginationInfo }}
        </span>
      </div>
      <v-btn
        class="ml-2"
        icon
        small
        :disabled="!paginationTracking.hasPreviousPage"
        @click="loadPreviousItems"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn
        class="ml-2"
        icon
        small
        :disabled="!paginationTracking.hasNextPage"
        @click="loadNextItems"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </v-skeleton-loader>
</template>

<script>
import DeviceGaugeCluster from "@atoms/DeviceGaugeCluster.vue";
import ButtonView from "@atoms/ButtonView.vue";
import MenuManageDevice from "./MenuManageDevice.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import PossiblePMS from "@components/common/PossiblePMS.vue";

import useDataTable from "@components/common/useDataTable";
import useInventory from "../useInventory";
import useFacility from "../../authentication/useFacility";
import {
  getPreinstalledInventoryHeaders,
  commonCustomFilter,
  commonCustomSort,
  createPrintMethods,
  createExportMethods,
  createInventoryWatcher,
  getCommonInventoryData,
  createInventoryExportData,
  COMMON_FOOTER_PROPS,
} from "../helper";

export default {
  name: "InventoryPreinstalledTable",
  components: {
    DeviceGaugeCluster,
    ButtonView,
    ButtonPrint,
    ButtonExportCSV,
    MenuManageDevice,
    PossiblePMS,
  },
  props: {
    search: {
      type: String,
      default: "",
    },
  },
  setup() {
    const {
      loading,
      preinstalledInventory,
      preinstalledPaginationTracking,
      preinstalledItemsPerPage,
      loadNextPage: inventoryLoadNextPage,
      loadPreviousPage: inventoryLoadPreviousPage,
      updateItemsPerPage: inventoryUpdateItemsPerPage,
      formatDeviceName,
    } = useInventory();

    const { currentFacility } = useFacility();
    const { tableState } = useDataTable({
      itemsPerPage: preinstalledItemsPerPage.value,
    });

    return {
      loading,
      inventory: preinstalledInventory,
      paginationTracking: preinstalledPaginationTracking,
      tableState,
      loadNextPage: () => inventoryLoadNextPage(1),
      loadPreviousPage: () => inventoryLoadPreviousPage(1),
      inventoryUpdateItemsPerPage,
      formatDeviceName,
      currentFacility,
    };
  },
  data() {
    return {
      ...getCommonInventoryData(),
      ...createInventoryExportData(),
    };
  },
  computed: {
    headers() {
      return getPreinstalledInventoryHeaders();
    },
    footerProps() {
      return COMMON_FOOTER_PROPS;
    },
    paginationInfo() {
      const { currentPage, pageSize, totalCount } = this.paginationTracking;
      if (totalCount === 0) return "0 items";

      const startItem = (currentPage - 1) * pageSize + 1;
      const endItem = Math.min(currentPage * pageSize, totalCount);

      return `${startItem}-${endItem} of ${totalCount}`;
    },
  },
  watch: {
    "tableState.itemsPerPage"(newValue) {
      if (!this.search) {
        this.inventoryUpdateItemsPerPage(
          1,
          newValue,
          this.$apollo.provider.defaultClient
        );
      }
    },
    search: {
      handler(newValue, oldValue) {
        if (oldValue && !newValue) {
          this.inventoryUpdateItemsPerPage(
            1,
            this.tableState.itemsPerPage,
            this.$apollo.provider.defaultClient
          );
        }
      },
      immediate: false,
    },

    ...createInventoryWatcher(),
  },
  methods: {
    customFilter: commonCustomFilter,
    customSort: commonCustomSort,

    ...createPrintMethods(1, function (newValue) {
      this.inventoryUpdateItemsPerPage(
        1,
        newValue,
        this.$apollo.provider.defaultClient
      );
    }),

    ...createExportMethods(1),

    gotoUnit(unitId) {
      this.$router.push({
        name: "ViewSmartUnit",
        params: {
          zone_id: unitId,
        },
      });
    },

    loadNextItems() {
      this.loadNextPage();
    },

    loadPreviousItems() {
      this.loadPreviousPage();
    },
    shouldShowProLabel(item) {
      try {
        if (!item || !item.profile) {
          return false;
        }
        const facility = this.currentFacility?.value || this.currentFacility;
        if (!facility || !facility.profile) {
          return false;
        }
        const facilityProfile = facility.profile;
        const isUnitLevelEnabled =
          facilityProfile?.muteAlertsDuring === "day" &&
          (facilityProfile?.muteAlertsOrganizationType ?? "facility") ===
            "zone";

        return isUnitLevelEnabled && item.profile?.muteAlertsDuringDay === true;
      } catch (error) {
        console.warn("shouldShowProLabel error:", error);
        return false;
      }
    },
  },
};
</script>

<style>
.custom-pagination .v-data-footer__icons-before,
.custom-pagination .v-data-footer__icons-after {
  visibility: hidden !important;
}
.custom-pagination .v-data-footer__pagination {
  display: none !important;
}
.preinstalled-pagination-arrow {
  position: absolute;
  bottom: -5px;
  right: 70px;
  z-index: 1;
}
</style>
